import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Box from '../UI/Box'
import { BuilderContext } from '../../app/builder/[id]/utils'
import {
  AiOutlineClose,
  AiOutlineDesktop,
  AiOutlineMobile,
  AiOutlineZoomIn,
  AiOutlineZoomOut,
} from 'react-icons/ai'
import routes from '../../routes'
import styled from 'styled-components'
import IconButton from '../UI/IconButton'
import Flex from '../UI/Flex'
import { Color } from '../../interfaces/designSystem'
import useMediaQuery from '@utils/useMediaQuery'
import { ColorPicker } from 'antd'
import { saveWidgetBackgroundColor } from '@api/widgets'

// const FULL_WIDTH = 1040
const MOBILE_WIDTH = 375
const MAX_ZOOM = 100
const MIN_ZOOM = 30

const StyledIframe = styled.iframe<{
  $zoom: number
  $desktop: boolean
  $fullWidth: number
  $bg: string
}>`
  border: none;
  display: block;
  //height: 0%;
  padding: 32px 0;
  height: calc(100vh - 185px);
  transform: scale(${props => props.$zoom});
  margin: 0 auto 0
    ${props =>
      props.$desktop ? `-${(props.$fullWidth * (1 - props.$zoom)) / 2}px` : `auto`};
  width: ${props =>
    props.$desktop
      ? `calc(100% + ${props.$fullWidth * (1 - props.$zoom)}px)`
      : `${MOBILE_WIDTH}px`};
  background-color: ${props => props.$bg};
`

const Wrap = styled(Box)`
  @media (max-width: 980px) {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 5;
    height: 100%;
    background-color: ${Color.White};
  }
`

const CloseIcon = styled(IconButton)`
  display: none;
  @media (max-width: 980px) {
    display: block;
  }
`

const Left = styled(Flex)`
  @media (max-width: 550px) {
    flex-direction: column;
  }
`

type Props = {
  open: boolean
  onClose: () => void
}

const WidgetPreviewWrapper = ({ open, onClose }: Props) => {
  const ref = useRef<HTMLIFrameElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const { widget } = useContext(BuilderContext)
  const [desktop, setDesktop] = useState(true)
  const [zoom, setZoom] = useState(80)
  const isMobile = useMediaQuery('(max-width: 980px)')
  const cardsCount = widget.config.cards.length
  const fullWidth = cardsCount * MOBILE_WIDTH
  const [backgroundColor, setBackgroundColor] = useState<string>(
    widget.backgroundColor || '#fff',
  )

  const onEventListener = event => {
    if (event.data.type === 'ready') {
      event.source.postMessage({ type: 'widgetUpdate', payload: widget }, '*')
    }
  }

  useEffect(() => {
    ref.current?.contentWindow?.postMessage(
      { type: 'widgetUpdate', payload: widget },
      '*',
    )
  }, [widget, open, isMobile])

  useEffect(() => {
    window.addEventListener('message', onEventListener, false)

    return () => {
      window.removeEventListener('message', onEventListener, false)
    }
  }, [open, isMobile, widget])

  const updateZoom = useCallback(() => {
    const width = wrapRef.current?.clientWidth

    if (width) {
      const z = ((width - 32) / (desktop ? fullWidth : MOBILE_WIDTH)) * 100
      let formatted = Math.floor(z / 10) * 10

      if (formatted < MIN_ZOOM) {
        formatted = MIN_ZOOM
      } else if (formatted > MAX_ZOOM) {
        formatted = MAX_ZOOM
      }

      setZoom(formatted)
    }
  }, [wrapRef, setZoom, desktop])

  useEffect(() => {
    updateZoom()
  }, [wrapRef, desktop])

  if (!open && isMobile) {
    return null
  }

  return (
    <Wrap p={16} width="100%" ref={wrapRef}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        zIndex={10}
        position="relative"
        mb={8}
      >
        <Left>
          <Flex>
            <Box p={10} borderRadius={8} bg={desktop ? undefined : Color.DarkGrey}>
              <IconButton
                icon={AiOutlineMobile}
                iconSize={30}
                onClick={() => setDesktop(false)}
                color={desktop ? Color.DarkGrey : Color.White}
              />
            </Box>
            <Box p={10} borderRadius={8} bg={!desktop ? undefined : Color.DarkGrey}>
              <IconButton
                icon={AiOutlineDesktop}
                iconSize={30}
                onClick={() => setDesktop(true)}
                color={!desktop ? Color.DarkGrey : Color.White}
              />
            </Box>
          </Flex>
          <Flex alignItems="center" ml={16}>
            Background color &nbsp;
            <ColorPicker
              value={backgroundColor}
              onChange={(_, hex) => {
                setBackgroundColor(hex)
              }}
              onOpenChange={pickerOpened => {
                if (!pickerOpened && backgroundColor) {
                  saveWidgetBackgroundColor(widget.id!, backgroundColor)
                }
              }}
            />
          </Flex>
        </Left>
        <Flex alignItems="center">
          <Box p={10} borderRadius={8}>
            <IconButton
              icon={AiOutlineZoomOut}
              iconSize={30}
              disabled={zoom <= MIN_ZOOM}
              onClick={() => {
                setZoom(prev => prev - 10)
              }}
            />
          </Box>
          <Box p={10} borderRadius={8}>
            <IconButton
              icon={AiOutlineZoomIn}
              iconSize={30}
              disabled={zoom >= MAX_ZOOM}
              onClick={() => {
                setZoom(prev => prev + 10)
              }}
            />
          </Box>
          <Box p={10}>{zoom}%</Box>
        </Flex>
        <CloseIcon icon={AiOutlineClose} onClick={onClose} />
      </Flex>

      <StyledIframe
        src={routes.publicRoutes.widgetPreviewIframe}
        $desktop={desktop}
        $fullWidth={fullWidth}
        ref={ref}
        $zoom={+(zoom / 100).toFixed(2)}
        $bg={backgroundColor}
      />
    </Wrap>
  )
}

export default WidgetPreviewWrapper
